import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from "@angular/router";
import { inject } from "@angular/core";
import { ErrorPageService } from "../modules/shared/services/error-page.service";
import { firstValueFrom } from "rxjs";
import { InstanceManagerService } from "../core/services/instance-manager.service";
import { UserService } from "../core/services/user.service";
import { TimsTokenService } from "../core/services/tims-token.service";
import { OktaAuthGuard } from "@okta/okta-angular";

export const canActivateInstance: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const instanceManagerService = inject(InstanceManagerService);
  const errorService = inject(ErrorPageService);

  if (instanceManagerService.instanceIdSnapshot && instanceManagerService.instanceTokenSnapshot)
    return true;

  await firstValueFrom(instanceManagerService.instance$);
  if (!!instanceManagerService.instanceIdSnapshot && !!instanceManagerService.instanceTokenSnapshot) {
    return true;
  }
  else {
    await errorService.navigateToErrorPage(ErrorPageService.UnAuthorized);
  }

  return false;
};

export const canActivateProductManagement: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const userService = inject(UserService);
  const errorService = inject(ErrorPageService);

  if (await firstValueFrom(userService.hasGeneralAdminRole$)) return true;

  await errorService.navigateToErrorPage(ErrorPageService.UnAuthorized);
  return false;
};

export const canActivateTimsTokenUser: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const errorService = inject(ErrorPageService);
  const oktaAuthGuard = inject(OktaAuthGuard);
  const tokenService = inject(TimsTokenService);
  const instanceManagerService = inject(InstanceManagerService);

  const token = route.queryParamMap.get('token');

  if (token) {
    const collectionId = route.params.collectionId;

    if (token === tokenService.token && authorizeToken(tokenService, collectionId))
      return true;

    tokenService.token = token;

    if (await tokenService.validate() && authorizeToken(tokenService, collectionId))
      return true;
  } else {
    if (!await oktaAuthGuard.canActivate(route, state))
      return false;

    if (instanceManagerService.instanceIdSnapshot && instanceManagerService.instanceTokenSnapshot)
      return true;

    await firstValueFrom(instanceManagerService.instance$);
    if (!!instanceManagerService.instanceIdSnapshot && !!instanceManagerService.instanceTokenSnapshot) {
      return true;
    }
  }

  await errorService.navigateToErrorPage(ErrorPageService.UnAuthorized);
  return false;

  function authorizeToken(tokenService: TimsTokenService, collectionId: string): boolean {
    return tokenService.isValid && !tokenService.isExpired() && tokenService.hasCollection(collectionId);
  }
};